/**
 * Utilities for filtering item attributes based on retailer settings
 */

export interface ItemAttributesConfig {
  displayValueOnly?: (string | null)[] | null;
  displayKeyValue?: (string | null)[] | null;
}

/**
 * Check if a field name is the price field
 * GraphQL sends us 'price' as the field name
 *
 * @param name - Field name to check
 * @returns true if the field is 'price'
 */
const isPriceField = (name?: string): boolean => {
  return name?.toLowerCase() === 'price';
};

export interface ItemDetail {
  name: string;
  value: string;
}

/**
 * Filter item attributes based on retailer settings configuration
 * @param attributes - Array of item attributes from variant.info
 * @param config - ItemAttributesConfig from retailer settings
 * @returns Filtered array of attributes to display
 */
export const filterItemAttributes = (
  attributes: ItemDetail[] | undefined,
  config: ItemAttributesConfig | null | undefined,
): ItemDetail[] => {
  if (!attributes || attributes.length === 0) {
    return [];
  }

  // If no config, show all attributes except price (price handled separately)
  if (!config) {
    return attributes.filter((attr) => !isPriceField(attr.name));
  }

  const displayValueOnly =
    config.displayValueOnly
      ?.filter((v): v is string => v !== null)
      .map((v) => v.trim().toLowerCase())
      .filter(Boolean) ?? [];
  const displayKeyValue =
    config.displayKeyValue
      ?.filter((v): v is string => v !== null)
      .map((v) => v.trim().toLowerCase())
      .filter(Boolean) ?? [];

  // Combine both arrays to get all allowed attributes
  const allowedAttributes = new Set([...displayValueOnly, ...displayKeyValue]);

  // If config exists but both arrays are empty, show all non-price attributes
  // This prevents blank item details when config is present but not populated
  if (allowedAttributes.size === 0) {
    return attributes.filter((attr) => !isPriceField(attr.name));
  }

  // Filter attributes to only those in the allowed list (excluding price which is handled separately)
  return attributes.filter((attr) => {
    const attrName = attr.name?.toLowerCase();
    return (
      attrName && !isPriceField(attr.name) && allowedAttributes.has(attrName)
    );
  });
};

/**
 * Separate price from other attributes
 * @param attributes - Array of item attributes from variant.info
 * @returns Object with price and other details
 */
export const separatePriceFromAttributes = (
  attributes: ItemDetail[] | undefined,
): { price: string | null; details: ItemDetail[] } => {
  if (!attributes || attributes.length === 0) {
    return { price: null, details: [] };
  }

  return attributes.reduce(
    (acc: { price: string | null; details: ItemDetail[] }, cur) => {
      if (isPriceField(cur.name)) {
        acc.price = cur.value;
      } else {
        acc.details.push(cur);
      }
      return acc;
    },
    { price: null, details: [] },
  );
};
